import React, { useCallback, useContext, useEffect, useState } from "react";
import { graphql } from "react-relay";
import { Link, Redirect } from "react-router-dom";

import {
	faBullseyeArrow,
	faCog,
	faFileAlt,
	faPlus,
	faQuestionCircle,
	faSignOut,
	faThumbsUp,
	faUserPlus,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { motion, type MotionProps } from "framer-motion";
import moment from "moment";

import AccountStore from "~/account/AccountStore";
import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import { GenericInvite } from "~/invite";
import { signOut } from "~/lib/auth";
import { isIntuneActive } from "~/lib/intune/intune";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import DevMenu from "~/nav/DevMenu";
import { useLibraryNavigationFeatureFlagOn } from "~/nav/featureFlag";
import Avatar from "~/ui/legacy/Avatar";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import { NavContext } from "../..";
import { ReactComponent as AskFellowIcon } from "../../icons/ask-fellow-outline.svg";
import { ReactComponent as SearchIcon } from "../../icons/search-outline.svg";

import type { AccountPanelNavQuery, AccountPanelNavQuery$data } from "~/__generated__/AccountPanelNavQuery.graphql";

import stylesNavbarAccountDropdown from "~/layout/NavbarAccountDropdown.module.css";
import styles from "~/nav/panels/Account/AccountPanel.module.css";

const progressBarVariants: MotionProps["variants"] = {
	visible: ({ width, diff }: { width: number; diff: number }) => ({
		width: `${width}%`,
		backgroundColor: diff < 4 ? "#D44243" : "#FCBF12",
		transition: { ease: "easeInOut", duration: 0.8, delay: 1 },
	}),
	hidden: ({ fill }: { fill?: boolean }) => ({
		width: fill ? "0%" : "100%",
		backgroundColor: "#FCBF12",
	}),
};

const trialBarVariants: MotionProps["variants"] = {
	visible: ({ diff }: { diff: number }) => ({
		backgroundColor: diff < 4 ? "#FFCCCC" : "#FFE9B3",
		transition: { ease: "easeInOut", duration: 800, delay: 1000 },
	}),
	hidden: {
		backgroundColor: "#FFE9B3",
	},
};

type BillingAccount = AccountPanelNavQuery$data["account"]["billingAccount"];

function TrialProgressBar({ billingAccount }: { billingAccount: BillingAccount }) {
	const [pose, setPose] = useState("hidden");

	useEffect(() => {
		window.setTimeout(() => setPose("visible"), 0);
	});

	const {
		subscriptionTrialEnd,
		subscriptionState,
		subscriptionTrialLength,
		subscriptionLabel,
		subscriptionProductName,
	} = billingAccount;
	if (!subscriptionTrialEnd) return null;

	const trialEnd = moment(subscriptionTrialEnd);
	const dayDiff = trialEnd.diff(moment(), "days");

	if (subscriptionState === "POST_TRIAL") {
		return (
			<div className={styles.trialBanner}>
				<div className={styles.trialBar}>
					<div className={styles.trialLabel}>{subscriptionLabel}</div>
					<div className={styles.trialProgress}>
						<motion.div
							animate={pose}
							className={styles.trialFull}
							variants={progressBarVariants}
							custom={{ diff: dayDiff, fill: true, width: 100 }}
						/>
						<motion.div variants={trialBarVariants} animate={pose} custom={{ diff: 0 }} className={styles.trialEmpty} />
					</div>
				</div>
				Your workspace trial has{" "}
				<strong>
					<i>expired.</i>
				</strong>{" "}
				As of {trialEnd.format("MMM D, YYYY")}, your workspace has been downgraded to our free plan.
			</div>
		);
	}

	return (
		<div className={styles.trialBanner}>
			<div className={styles.trialBar}>
				<div className={styles.trialLabel}>{subscriptionLabel}</div>
				<div className={styles.trialProgress}>
					<motion.div
						variants={progressBarVariants}
						animate={pose}
						className={styles.trialFull}
						custom={{
							fill: true,
							diff: dayDiff,
							width: ((subscriptionTrialLength - dayDiff) / subscriptionTrialLength) * 100,
						}}
					/>
					<motion.div
						variants={trialBarVariants}
						animate={pose}
						custom={{ diff: dayDiff }}
						className={styles.trialEmpty}
					/>
				</div>
			</div>
			Your workspace <strong>{subscriptionProductName}</strong> trial has{" "}
			<strong>
				<i>
					{dayDiff} day{dayDiff > 1 && "s"} remaining.
				</i>
			</strong>{" "}
			On {trialEnd.format("MMM D, YYYY")}, your workspace will be downgraded to our free plan.
		</div>
	);
}

interface AccountIconProps {
	/** This is just because Avatar requires it */
	id: string;
	name: string;
	logo: string;
	uri: string;
	/** whether this account is the one we're currently on – if true, the icon won't be a link */
	active?: boolean;
}

function AccountIcon({ id, name, logo, uri, active = false }: AccountIconProps) {
	const className = cx(styles.accountIcon, {
		[styles.accountIconActive]: active,
	});

	const image = <Avatar user={{ id, fullName: name, image: logo }} width="2.5rem" />;

	if (active) {
		return <div className={className}>{image}</div>;
	} else {
		return (
			<a href={uri + window.location.pathname} className={className}>
				{image}
			</a>
		);
	}
}

function navigateToLoginView() {
	const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
	window.location.href = `https://${rootDomain.slice(1)}/auth/login/`;
}

function onSignOut(e: React.MouseEvent) {
	e.preventDefault();
	signOut();
}

function AccountPanelInner({ toggleMockIntercomBubble }: { toggleMockIntercomBubble?: () => void }) {
	const isLibraryFeatureFlagOn = useLibraryNavigationFeatureFlagOn();
	const queryData = useLazyLoadQuery<AccountPanelNavQuery>(
		graphql`
			query AccountPanelNavQuery {
				viewer {
					hasAdminAccess
					hasAccessToCopilot
				}
				user {
					id
					fullName
					image
					firstName
					lastName
					email
					isGuest
				}
				account {
					id
					name
					logo
					uri
					subdomain
					billingAccount {
						subscriptionLabel
						subscriptionProductName
						subscriptionState
						subscriptionTrialEnd
						subscriptionTrialLength
						subscriptionTrialIsSoft
						product {
							family
						}
					}
				}
			}
		`,
		{},
		{},
	);

	const { isMobile } = useContext(ViewportContext);
	const { dispatchPanel } = useContext(NavContext);

	const setModalOpen = useCallback(() => {
		return __globalModalContext?.open(({ close }) => (
			// eslint-disable-next-line camelcase
			<GenericInvite onClose={close} analytics={{ ux_location: "navbar__mobile_overflow" }} />
		));
	}, []);

	const { openSettingsModal } = useContext(AppContext);
	const openUserSettings = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			dispatchPanel("close");
			openSettingsModal();
		},
		[dispatchPanel, openSettingsModal],
	);

	const { account, user, viewer } = queryData;

	const openFeedback = React.useCallback(() => {
		dispatchPanel("feedback");
	}, [dispatchPanel]);

	const openSearch = React.useCallback(() => {
		dispatchPanel("search");
	}, [dispatchPanel]);

	const openAskFellow = React.useCallback(() => {
		dispatchPanel("ask-fellow");
	}, [dispatchPanel]);

	if (!isMobile) {
		if (user.isGuest) return <Redirect to="/stream/" />;
		return <Redirect to="/home/" />;
	}

	const isIntune = isIntuneActive();

	const otherAccounts = AccountStore.getOthers();
	const { subscriptionLabel, subscriptionState, subscriptionTrialEnd, product } = account.billingAccount;

	return (
		<div className={styles.overflowPage}>
			<div className={styles.accountSection}>
				<div className={styles.accountSwitcherWrapper}>
					<div className={styles.accountSwitcher}>
						<AccountIcon name={account.name} logo={account.logo} uri={account.uri} id={account.id} active />
						{!isIntune &&
							otherAccounts.map(({ id, name, logo, uri }) => (
								<AccountIcon key={id} name={name} logo={logo} uri={uri} id={id} />
							))}
					</div>
					{!isIntune && (
						<div className={styles.plusButtonContainer}>
							<button className={styles.plusButton} onClick={navigateToLoginView}>
								<FontAwesomeIcon icon={faPlus} title="Log into another workspace" />
							</button>
						</div>
					)}
				</div>
				<div className={styles.accountData}>
					<div className={styles.accountName}>
						{account?.name}
						<span
							className={cx(
								styles.subscriptionTag,
								stylesNavbarAccountDropdown.plan,
								stylesNavbarAccountDropdown[product.family],
							)}
						>
							{subscriptionLabel} plan
						</span>
					</div>
					<span>
						{account.subdomain}
						{window.ROOT_DOMAIN ?? ".fellow.app"}
					</span>
				</div>
			</div>
			{["TRIAL", "POST_TRIAL"].includes(subscriptionState) && subscriptionTrialEnd && (
				<>
					<hr />
					<TrialProgressBar billingAccount={account.billingAccount} />
				</>
			)}
			<hr />
			<div>
				<div className={styles.overflowLink}>
					<Avatar user={user} link={false} width="2rem" className={styles.overflowLinkIcon} />
					<div className={styles.userData}>
						<span className={styles.overflowLinkText}>{user.fullName}</span>
						<span className={styles.overflowLinkText}>{user.email}</span>
					</div>
				</div>
				<Link to="/settings/" className={styles.overflowLink} onClick={openUserSettings}>
					<FontAwesomeIcon icon={faCog} className={styles.overflowLinkIcon} />
					<span className={styles.overflowLinkText}>User settings</span>
				</Link>
				{viewer.hasAdminAccess && (
					<Link to="/account/settings" className={styles.overflowLink}>
						<FontAwesomeIcon icon={faCog} className={styles.overflowLinkIcon} />
						<span className={styles.overflowLinkText}>Workspace settings</span>
					</Link>
				)}
			</div>
			<hr />
			<div>
				<HelpCenterLink className={styles.overflowLink}>
					<FontAwesomeIcon icon={faQuestionCircle} className={styles.overflowLinkIcon} />
					<span className={styles.overflowLinkText}>Help</span>
				</HelpCenterLink>
				{!user.isGuest && (
					<Link to="/templates-section/" className={styles.overflowLink}>
						<FontAwesomeIcon icon={faFileAlt} className={styles.overflowLinkIcon} />
						<span className={styles.overflowLinkText}>Templates</span>
					</Link>
				)}
				<a onClick={openFeedback} className={styles.overflowLink}>
					<FontAwesomeIcon icon={faThumbsUp} className={styles.overflowLinkIcon} />
					<span className={styles.overflowLinkText}>Feedback</span>
				</a>
				{viewer.hasAccessToCopilot && (
					<a onClick={openAskFellow} className={styles.overflowLink}>
						<div className={styles.overflowLinkIcon}>
							<AskFellowIcon />
						</div>
						<span className={styles.overflowLinkText}>Ask Fellow</span>
					</a>
				)}
				{isLibraryFeatureFlagOn && (
					<>
						<Link to="/objectives/" className={styles.overflowLink}>
							<div className={styles.overflowLinkIcon}>
								<FontAwesomeIcon icon={faBullseyeArrow} className={styles.overflowLinkIcon} />
							</div>
							<span className={styles.overflowLinkText}>Objectives</span>
						</Link>
						<a onClick={openSearch} className={styles.overflowLink}>
							<div className={styles.overflowLinkIcon}>
								<SearchIcon />
							</div>
							<span className={styles.overflowLinkText}>Search</span>
						</a>
					</>
				)}
			</div>
			<hr />
			<div>
				{!user.isGuest && (
					<a href="#" onClick={setModalOpen} className={styles.overflowLink}>
						<FontAwesomeIcon icon={faUserPlus} className={styles.overflowLinkIcon} />
						<span className={styles.overflowLinkText}>Invite teammates</span>
					</a>
				)}
				<a href="/auth/logout" onClick={onSignOut} className={styles.overflowLinkRed}>
					<FontAwesomeIcon icon={faSignOut} className={styles.overflowLinkIcon} />
					<span className={styles.overflowLinkText}>Sign out</span>
				</a>
				{window.ENVIRONMENT !== "prod" && isMobile && (
					<DevMenu inPanel toggleMockIntercomBubble={toggleMockIntercomBubble} />
				)}
			</div>
		</div>
	);
}

export default withSuspense(null, AccountPanelInner);
